import { useTVConnect } from "@/store/modules/media/tv-connect";
import * as io from "socket.io-client";

import { Options, Vue } from "vue-class-component";
import { socket_control } from "@/config/host.config";
import { AccessToken } from "@/api/accessDecoder";

import type { IComandData, TVInRoom, TVPlaybackStatus } from "@/@types/connection/tvs";
import { refreshAccessToken } from "@/api/authorization/auth-request-api";

interface ISelectedTVState {
  isSelected: boolean;
  selectedTVId: string;
  requestedTVId: string;
  tvInfo?: {
    _id: string;
    tv_name: string;
    rules: "tvuser" | "stb" | string; // можно уточнить возможные значения
    isSTB: boolean;
    linkedTVName: string;
    isOnline: boolean;
  }; // Опционально, присутствует только когда isSelected = true
  timestamp: number;
}

@Options({ name: "connect" })
class Connect extends Vue {
  socket: io.Socket | null = null;

  command_name = "";
  command_data: IComandData | null = null;

  // Объявляем пропс для callback'а
  onAvailableTVs?: () => void;

  private supervisionInterval: ReturnType<typeof setInterval> | null = null;

  get selected_tv(): string {
    return useTVConnect().getActiveTV;
  }

  get available_tvs(): TVInRoom[] {
    return useTVConnect().getTVs;
  }

  get tv(): TVInRoom | null {
    const tv = this.available_tvs.find((t) => t._id === this.selected_tv);
    return tv ? tv : null;
  }

  start(): void {
    this.wsAuthError();
    this.selectedTVCheckResult();
    this.selectedTVCheckError();
    this.wsAvailableTvs();
    this.wsTvSelected();
    this.wsTvStatus();
  }

  connect(): void {
    this.socket?.close();
    this.socket = null;

    console.log("NEW SOCKET");
    const base: Partial<io.ManagerOptions & io.SocketOptions> = {
      // auth-функция вызывается socket.io на КАЖДОЙ попытке (ре)коннекта —
      // токен всегда берётся свежим из localStorage, даже после простоя сервера
      auth: (cb: (data: Record<string, unknown>) => void) => {
        cb({ token: "Bearer " + AccessToken.access });
      },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      transports: ["websocket", "polling"], // Порядок транспортов
      forceNew: true, // Всегда новое соединение
    };
    // dev: абсолютный URL (http://host:3030) → прямой коннект (namespace "/", path "/socket.io")
    // prod: относительный путь (/socket/<name>) → Engine.IO path через прокси
    this.socket = /^(https?:)?\/\//i.test(socket_control.magic_remote)
      ? io.connect(socket_control.magic_remote, base)
      : io.connect({ ...base, path: socket_control.magic_remote });

    // При каждом (пере)подключении заново получаем список доступных ТВ
    this.socket.on("connect", () => this.wsGetAvailableTvs());

    this.watchServerKick();
    this.startSupervision();

    this.start();
    if (this.command_name) this.sendNewCommand(this.command_name, this.command_data);
  }

  private watchServerKick(): void {
    const socket = this.socket;
    if (!socket) return;
    socket.on("disconnect", (reason: string) => {
      if (reason !== "io server disconnect") return;
      setTimeout(() => {
        if (this.socket === socket && !socket.connected) this.connect();
      }, this.$second(3));
    });
  }

  private startSupervision(): void {
    if (this.supervisionInterval) return;
    this.supervisionInterval = setInterval(() => {
      if (!this.socket?.connected) this.connect();
    }, this.$second(30));
  }

  beforeUnmount(): void {
    if (this.supervisionInterval) clearInterval(this.supervisionInterval);
    this.supervisionInterval = null;
  }

  wsAuthError(): void {
    if (!this.socket) return this.connect();
    this.socket.on("connect_error", async (error: Error) => {
      const message = (error.message || "").toLowerCase();
      const isAuthError = /unauthor|forbidden|token|jwt/.test(message);

      if (!isAuthError) {
        // Транспортная ошибка (сервер недоступен): socket.io сам переподключится
        // с бэкоффом, auth-функция возьмёт свежий токен при следующей попытке.
        console.error("Connection error:", error.message);
        return;
      }

      // Сервер отклонил токен (за время простоя access истёк) — обновляем
      console.log("wsAuthError connect_error");
      try {
        await refreshAccessToken();
        // Токен обновлён — форсируем немедленное переподключение
        this.socket?.connect();
      } catch (refreshError) {
        // 400/401 → logout уже выполнен внутри refreshAccessToken;
        // сетевой сбой — следующую попытку сделает сам socket.io
        console.error(refreshError);
      }
    });
  }

  wsGetAvailableTvs(): void {
    if (!this.socket) return this.connect();
    this.socket.emit("get-available-tvs");
  }

  /**
   * Запрашивает список доступных ТВ (например, при входе на страницу каналов).
   * Если ws ещё не создан — подключается, запрос уйдёт в обработчике connect.
   */
  loadAvailableTvs(): void {
    if (!this.socket?.connected) return this.connect();
    this.wsGetAvailableTvs();
  }

  /**
   * Прогресс воспроизведения с ТВ (название, время, длительность) —
   * приходит каждую секунду, пока на ТВ что-то играет.
   * Применяем только статус от выбранного ТВ.
   */
  wsTvStatus(): void {
    if (!this.socket) return this.connect();
    this.socket.on(
      "tv-status",
      (data: { status?: { playback?: TVPlaybackStatus | null }; from?: string }) => {
        if (data?.from && data.from !== this.selected_tv) return;
        useTVConnect().setTvPlayback(data?.status?.playback ?? null);
      }
    );
  }

  wsAvailableTvs(): void {
    if (!this.socket) return this.connect();
    this.socket.on("available-tvs", (data: Array<TVInRoom>) => {
      useTVConnect().setTVs(data);
      // Нет ни одного ТВ — селектор не открываем, устаревший выбор не переизбираем
      if (!data.length) return;
      if (data.length === 1) {
        this.wsSelectedTv(data[0]._id);
        return;
      }
      if (!this.selected_tv && this.onAvailableTVs) {
        this.onAvailableTVs();
      } else {
        this.wsSelectedTv(this.selected_tv);
      }
    });
  }

  // -----------------------------------------------------
  sendNewCommand(command_name: string, command_data: IComandData | null): void {
    console.log("--------" + command_name + "---------");
    console.log(JSON.stringify(command_data));

    this.command_data = command_data;
    this.command_name = command_name;

    // Открыли с планшета фильм — показывать аудиотрек на пульте больше
    // не нужно: сбрасываем сразу, не дожидаясь статуса tv-status от ТВ
    if (command_name === "movie_open") useTVConnect().setTvPlayback(null);

    if (!this.socket) return this.connect();
    this.socket.emit("check-selected-tv", { tvId: this.selected_tv });
  }

  selectedTVCheckResult(): void {
    if (!this.socket) return this.connect();
    console.log("selectedTVCheckResult");
    this.socket.on("selected-tv-check-result", (data: ISelectedTVState) => {
      console.log("ON: selected-tv-check-result");
      console.log(data);

      if (data.isSelected) this.wsSelectedTvOn();
      else this.wsGetAvailableTvs();
    });
  }

  selectedTVCheckError(): void {
    if (!this.socket) return this.connect();

    this.socket.on(
      "selected-tv-check-error",
      (data: { isSelected: boolean; error: string; code: string }) => {
        if (!data.isSelected) console.log("selected-tv-check-error" + data.code);
        this.wsGetAvailableTvs();
        console.log("selected-tv-check-error");
      }
    );
  }

  // -----------------------------------------------------

  wsSelectedTv(tvId: string): void {
    useTVConnect().setactiveTV(tvId);
    if (!this.socket) return this.connect();
    this.socket.emit("select-tv", { tvId });
  }

  wsCommand(): void {
    console.log("wsCommand");
    console.log("command_name: " + this.command_name);

    // Список ТВ может загружаться заранее (например, на странице каналов),
    // без ожидающей команды отправлять нечего
    if (!this.command_name) return;

    if (!this.socket) return this.connect();
    this.socket.emit("control", {
      command: this.command_name,
      data: this.command_data,
      targetTVId: this.selected_tv,
    });
  }

  tvPower(): void {
    if (!this.socket) return this.connect();
    this.socket.emit("wake_on_lan", {
      tvId: this.selected_tv,
    });
  }

  wsTvSelected(): void {
    if (!this.socket) return this.connect();
    this.socket.on("tv-selected", () => {
      console.log("wsTvSelected");
      this.wsSelectedTvOn();
    });
  }

  wsSelectedTvOn(): void {
    if (!this.socket) return this.connect();
    // this.$emit("open_magic_remote");
    // if (this.tv && this.tv.linked && this.tv.linked.id) {
    //   console.log(this.tv.linked.id);

    //   this.$emit(
    //     "snatdartTVComand",
    //     this.command_name,
    //     this.tv.linked.id,
    //     this.command_data
    //   );
    //   return;
    // }

    this.wsCommand();
  }
}
export default Connect;
